<template>
  <div>

    <v-card>
      <v-card-title class="headline">Insights</v-card-title>

      <v-card-text>      
        <p>
          Key metrics are calculated weekly. Reports are always generated based on Monday - Sunday weekly intervals.
        </p>
      </v-card-text>

      <v-card-text v-if="insights">
        <h4>
          Key Metrics
          <v-dialog v-if="insights.snapshots.length > 1" v-model="showPeriodDialog" max-width="400px">
            <template v-slot:activator="{ on }">
              <v-btn text small color="primary" class="" v-on="on">select period</v-btn>
            </template>
            <v-card>
              <v-card-title>Select Period</v-card-title>
              <v-card-text>
                Select the period to view.
              </v-card-text>
              <v-divider></v-divider>
              <v-list>
                <template v-for="(item, idx) in insights.snapshots">
                  <v-list-item :key="idx">
                    <v-list-item-content>
                    <v-list-item-title>{{item.from | localDate('D', true) }} - {{item.till | localDate('D', true) }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn text color="primary" @click="snapshot=item;showPeriodDialog = false;"> {{$t('shared.select')}}</v-btn>
                    </v-list-item-action>
                  </v-list-item>
                  <v-divider  :key="`sep-${idx}`"/>
                </template>
              </v-list>
              <v-card-actions>
                <v-btn color="secondary" text @click="showPeriodDialog = false">{{$t('shared.cancel')}}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>           
        </h4>
        <p v-if="snapshot">
          Based on period running from 
          {{snapshot.from | localDate('ddd L', true) }}
          until
          {{snapshot.till | localDate('ddd L', true) }} (included).
        </p>
        <v-alert v-if="snapshot && snapshot.partial" type="info" text>
          Reporting is based on a partial week. 
          This might impact the results of this period compared to other periods.
        </v-alert>
        <p v-if="!snapshot">
          We don't have data for the current period yet. Sync some data so we can provide you with relevant insights.
        </p>
      </v-card-text>

      <v-card-text v-if="insights">
        <v-divider class="mt-4 mb-4"/>
        <h4>Today</h4>
        <v-row>
          <v-col cols="12" sm="4">
            <v-progress-circular :value="(insights.today.current / insights.today.goal) * 100" rotate="-90" size="200" color="primary" width="10">
              <template v-slot:default> {{ insights.today.current | int }} out of {{ insights.today.goal | int }} </template>
            </v-progress-circular>
          </v-col>
          <v-col cols="12" sm="8">
            <h4>Activities</h4>
            <v-divider />
            <v-list v-if="insights.today.activities" class="pa-0" dense>
              <template v-for="(item, idx) in insights.today.activities">
                <v-list-item :key="`div_${idx}`" >
                  <v-list-item-avatar>
                    <v-icon>{{$helpers.getActivityIcon(item.t)}}</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ $helpers.getActivityTypeLabel(item.t) }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.t == 'DAILY_SUMMARY'">{{ item.s | steps }} steps → {{ item.pt | int }} points</v-list-item-subtitle>
                    <v-list-item-subtitle v-else>{{ item.d | duration }} → {{ item.pt | int }} points</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-divider :key="`div_${idx}`" />
              </template>
            </v-list>
            <p v-else class="text-muted">No activities of today (yet). Please note there might be a 10-15 minute processing delay after syncing data.</p>
          </v-col>
        </v-row>
        <v-divider class="mt-4 mb-4"/>
        <h4>This Week</h4>
        <v-row>
          <v-col v-for="(item, idx) in insights.week.days" :key="`day_${idx}`">
            <v-card :flat="item.future" :color="item.today ? 'grey lighten-4' : 'white'" class="text-center d-flex flex-row" style="overflow:hidden;">
              <div class="d-flex flex-column grow">
                <v-card-title :class="{'d-inline-block pb-2 text-muted': true, 'black--text': item.today}">{{ item.day }}</v-card-title>
                <v-card-text v-if="!item.future">
                  <div v-if="item.completed">✔️</div>
                  <div v-else>{{Math.round((item.points / item.goal) * 100)}}%</div>
                  <div class="mt-2 text-muted">{{ item.points | int }} <span class="">pts</span></div>
                </v-card-text>
              </div>
              <div v-if="!item.future" class="d-flex" style="width:5px; flex-align: end; flex-direction: column-reverse; background-color: white;">
                <!-- <div class="d-inline-block" :style="`background-color:white; flex-grow: ${item.goal - item.points};`">&nbsp;</div>
                <div class="d-inline-block" :style="`background-color:var(--v-primary-base); flex-grow: ${item.points};`">&nbsp;</div> -->
                <div class="d-inline-block" :style="`background-color:var(${getColorCssVar(item)}); height: ${(item.points/item.goal)*100}%;`">&nbsp;</div>
              </div>
            </v-card>
          </v-col>
        </v-row>
        <v-divider class="mt-4 mb-4"/>
        <p v-if="insights.today.last_update" class="text-muted">
          Last updated on: {{ insights.today.last_update | localDate('ddd L LTS Z') }}
        </p>
        <p v-else class="text-muted">
          We don't have data for today yet.
        </p>
      </v-card-text>

      <v-container class="mt-0" v-if="insights && snapshot">
        <v-row>
          <v-col cols="12" sm="4" md="6">
            <v-card class="white--text" color="secondary darken-2">
              <v-card-text class="white--text">League</v-card-text>
              <v-card-title v-if="insights.previous.tier" class="subtitle">{{ insights.tiers.find(x => x.value == insights.previous.tier).name }}</v-card-title>
              <v-card-title v-else class="subtitle">n/a</v-card-title>
              <!-- <p v-if="snapshot.points_till_next_tier" class="subtitle">Points needed to advance to next league: {{ snapshot.points_till_next_tier }}</p> -->
            </v-card>
          </v-col>
          <v-col cols="12" sm="4" md="6">
            <v-card class="white--text" color="primary darken-2">
              <v-card-text class="white--text">Daily Goal</v-card-text>
              <v-card-title v-if="snapshot.daily_goal" class="subtitle">{{ snapshot.daily_goal | int }} points</v-card-title>
              <v-card-title v-else class="subtitle">n/a</v-card-title>
              <!-- <p v-if="snapshot.points_till_next_tier" class="subtitle">Points needed to advance to next league: {{ snapshot.points_till_next_tier }}</p> -->
            </v-card>
          </v-col>
          <v-col cols="12" sm="4" md="6">
            <v-card class="white--text" color="primary darken-2">
              <v-card-text class="white--text">Days remaining</v-card-text>
              <v-card-title v-if="snapshot.days_remaining" class="subtitle">{{ snapshot.days_remaining }} days</v-card-title>
              <v-card-title v-else class="subtitle">n/a</v-card-title>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="4" md="6">
            <v-card class="white--text" color="secondary darken-2">
              <v-card-text class="white--text">Streak</v-card-text>
              <v-card-title v-if="insights.previous.streak_length" class="subtitle">{{ insights.previous.streak_length }} weeks</v-card-title>
              <v-card-title v-else class="subtitle">n/a</v-card-title>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-card-text v-if="insights && snapshot && snapshot.activity_stats">
        <h3>Activities</h3>

        <v-row>
          <v-col v-for="(item, idx) in snapshot.activity_stats" :key="idx" cols="12" sm="4" md="4">
            <v-card class="white--text" :color="`accent darken-${idx}`">
              <v-card-title class="white--text force-one-line" :title="$helpers.getActivityTypeLabel(item.type, event)">
                <v-icon color="white" small class="me-2">{{ $helpers.getActivityIcon(item.type) }}</v-icon> 
                {{ $helpers.getActivityTypeLabel(item.type, event) }}
              </v-card-title>
              <v-divider style="border-color: #ffffff33;" />
              <v-card-text v-if="item.type == 'DAILY_SUMMARY'" class="white--text pb-0">{{ item.count}} days</v-card-text>
              <v-card-text v-if="item.type != 'DAILY_SUMMARY'" class="white--text pb-0">{{ item.count}} activities</v-card-text>
              <v-card-text v-if="item.type == 'DAILY_SUMMARY'" class="white--text">{{ item.steps | steps }} steps</v-card-text>
              <v-card-text v-if="item.type != 'DAILY_SUMMARY'" class="white--text">
                <span v-if="item.distance" class="white--text">{{ item.distance | distance }} • </span>
                {{ item.time | duration }}
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>        
      </v-card-text>

      <v-card-text v-if="insights && snapshot && snapshot.points_day">
        <h3>Points by day</h3>
        <RaceHistogramByDate :data="snapshot.points_day" label="Points by day" :defaultColor="$vuetify.theme.defaults.light.primary"  :accentColor="$helpers.getColorVariant($vuetify.theme.defaults.light.primary, 100)" />
      </v-card-text>

    </v-card>
      <v-card-text v-if="insights">
        <v-header>Week-by-week breakdown</v-header>
        <div v-for="(item, idx) in insights.snapshots" :key="idx" class="d-flex ">
          <div class="d-flex px-2" style="flex: 200px;">
            <strong>Week:&nbsp;</strong>
            <div>
              {{item.from | localDate('D', true) }}
              -
              {{item.till | localDate('D', true) }}
              <span v-if="item.partial">(partial)</span>
            </div>
          </div>
          <div class="d-flex px-2" style="flex: 50px;">
            <span v-if="item.tier">{{item.tier }}</span>
            <span v-if="item.tier_score" class="text-muted">({{item.tier_score | int }} pts)</span>
          </div>
          <div class="d-flex px-2" style="flex: 50px;">
            <span v-if="item.streak_length">{{item.streak_length }} wk(s)</span>
          </div>
        </div>
      </v-card-text>

    </v-card>
  </div>      
</template>

<script>
import { mapGetters } from "vuex";
import RaceHistogramByDate from '@/components/RaceHistogramByDate.vue'
import profileService from "@/services/profileService";
import { EventBus } from '@/plugins/eventbus.js';
import siteData from '@/data/site.json'

export default {
  name: "Insights",
  components: {
    RaceHistogramByDate,
  },
  props: {
  },
  data() {
    return {
      siteData: siteData,
      insights: null,
      snapshot: null,
      showPeriodDialog: false,
    };
  },
  async mounted() {
    await this.getProfile();
    
    EventBus.$on('login-state-change', async user => {
      await this.getProfile();
    });
  },
  methods: {

    async getProfile() {
      if (this.user) {
        this.insights = (await profileService.getInsights(this.$route.query.date)).data;
        if (this.insights && this.insights.current) {
          this.snapshot = this.insights.current;
        }
      }
    },

    getColorCssVar(day) {
      if (day.today) {
        return '--v-info-base';
      }
      if (day.completed) {
        return '--v-success-base'
      }
      return '--v-warning-base';
    }
    // getStatsForRace(raceId) {
    //   return this.stats && this.stats.participants_by_race && this.stats.participants_by_race[raceId];
    // },

  },
  computed: {
    breadcrumbItems() {
      return !this.event ? [] : [
        { text: 'Profile', exact: true, to: { name: 'profile' } },
        { text: `Insights`, disabled: true },
      ];
    },

    ...mapGetters({
      user: "user"
    })
  },
};
</script>
<style lang="scss">
</style>

